import { isEmpty } from "lodash";
import * as warehouse from "@/warehouse";
import type { QueryInput } from "@/models/compound";
import type { GetFilteredTracesV2, GetFilteredSpansV2 } from "@/models/traces";
import type { GetFilteredScalarMetricsV2 } from "@/models/metrics";
import type { GetFilteredLogsV2 } from "@/models/logs";

/* Possible states for a given filename
new = The filename hasn't been used at all.
inProgress = There's a process currently processing this filename.
started = The filename has had some kind of processing before but is not being processed right now.
finished = The filename has been completely processed.
 */
type FilenameState = "new" | "inProgress" | "started" | "finished";

// Defines the current progress for a filename under a given query
export interface QueryProgressData {
  pageCursor: string | undefined;
  state: FilenameState;
  chunksLoaded: number;
}

// Default values here instead of Vuex to avoid circular importing
const defaultChunksPerFilename: Record<string, number> = {
  traces: 20,
  spans: 200,
  logs: 20,
  metrics: 20,
};

// Wrapper class around the warehouse used to handle the QueryData for a given view with multiple filenames
export class QueryData {
  sharedInput: QueryInput;
  progress: Map<string, QueryProgressData>;

  constructor(
    sharedInput: QueryInput,
    progress: Map<string, QueryProgressData>
  ) {
    this.sharedInput = Object.freeze(sharedInput);
    this.progress = progress;
  }

  static setQueryData(
    identifier: string,
    sharedInput: QueryInput,
    filenames: string[]
  ): void {
    const progressData = new Map<string, QueryProgressData>(
      filenames.map((filename) => [
        filename,
        {
          pageCursor: undefined,
          state: "new",
          chunksLoaded: 0,
        },
      ])
    );

    warehouse.set(
      `${identifier}-query-data`,
      new QueryData(sharedInput, progressData)
    );
  }

  static getQueryData(identifier: string): QueryData | undefined {
    const warehouseData = warehouse.get(`${identifier}-query-data`, {});
    return !isEmpty(warehouseData) ? (warehouseData as QueryData) : undefined;
  }

  static updateQueryData(
    identifier: string,
    queryInputToUpdate: QueryInput,
    completed = false
  ): void {
    const warehouseData = this.getQueryData(identifier);
    if (warehouseData === undefined) return;

    const filenameProgress = warehouseData.progress;
    const filenameToUpdate = queryInputToUpdate.filename;

    if (!Array.from(filenameProgress.keys()).includes(filenameToUpdate))
      // Return if the key was not found inside the progress object
      return;

    const previousValue = filenameProgress.get(filenameToUpdate);
    const newState = completed ? "finished" : "started";
    const newChunksLoaded = completed
      ? defaultChunksPerFilename[identifier]
      : (previousValue as QueryProgressData).chunksLoaded + 1;
    filenameProgress.set(filenameToUpdate, {
      pageCursor: queryInputToUpdate.pageCursor,
      state: newState,
      chunksLoaded: newChunksLoaded,
    });

    warehouseData.progress = filenameProgress;
    warehouse.set(`${identifier}-query-data`, warehouseData);
  }

  static deleteQueryData(identifier: string): void {
    warehouse.set(`${identifier}-query-data`, {});
  }

  static getQueryProgressValue(identifier: string): number {
    const warehouseData = QueryData.getQueryData(identifier);
    if (warehouseData === undefined) return 0;

    const progressValues = Array.from(warehouseData.progress.values());
    const chunksSum = progressValues.reduce(
      (acc, curr) => acc + curr.chunksLoaded,
      0
    );
    return (
      Math.round(
        (chunksSum /
          (defaultChunksPerFilename[identifier] * progressValues.length)) *
          1000
      ) / 10
    );
  }

  static completeProgressValue(identifier: string): void {
    QueryData.setQueryData(identifier, { filename: "foo" } as QueryInput, [
      "foo",
    ]);
    QueryData.updateQueryData(
      identifier,
      { filename: "foo" } as QueryInput,
      true
    );
  }

  static updateQueryProgressState(
    identifier: string,
    filename: string,
    newState?: FilenameState
  ) {
    const warehouseData = this.getQueryData(identifier);
    if (warehouseData === undefined) return;

    const filenameProgress = warehouseData.progress;
    if (!Array.from(filenameProgress.keys()).includes(filename))
      // Return if the key was not found inside the progress object
      return;

    const previousValue = filenameProgress.get(filename) as QueryProgressData;
    const stateToAssign = newState
      ? newState
      : previousValue.chunksLoaded === 0
      ? "new"
      : "started";
    filenameProgress.set(filename, {
      pageCursor: previousValue.pageCursor,
      state: stateToAssign,
      chunksLoaded: previousValue.chunksLoaded,
    });
  }

  static createQueryInput(
    identifier: string,
    sharedInput: QueryInput,
    filename: string,
    pageCursor: string | undefined
  ): QueryInput {
    const return_query_input = {
      ...sharedInput,
      filename: filename,
      pageCursor: pageCursor,
    };
    if ("spans" === identifier) {
      return return_query_input as GetFilteredSpansV2;
    } else if ("logs" === identifier) {
      return return_query_input as GetFilteredLogsV2;
    } else if ("metrics" == identifier) {
      return return_query_input as GetFilteredScalarMetricsV2;
    }

    // We define `traces` as the default identifier to be return, but validate before to throw an Error if the identifier doesn't match
    if ("traces" !== identifier)
      throw new Error(`The identifier ${identifier} is not valid`);

    return return_query_input as GetFilteredTracesV2;
  }
}
